import React, { useMemo } from 'react';
import SiteCharts from '../../../components/SiteCharts';
import { handinOverS1CellChartConfig } from '../cellChartConfigs';

/**
 * Props for the HandinOverS1Cell component
 */
interface HandinOverS1CellProps {
  /** Cell identifier passed to API as cell_name parameter */
  cellName: string;
  /** Current fullscreen state */
  isFullscreen: boolean;
  /** Callback function to toggle fullscreen mode */
  onFullscreenToggle: () => void;
}

/**
 * Handin Over S1 Handover Chart (LTE Cell Level)
 * 
 * Displays handover metrics for incoming handovers over the S1 interface for individual LTE cells.
 * Shows the number of successful incoming handovers from other cells.
 * 
 * This component is a thin wrapper around the SiteCharts template component,
 * configured with cell-level S1 handin metrics from cellChartConfigs.ts
 * 
 * @component
 * @example
 * ```tsx
 * <HandinOverS1Cell
 *   cellName="173716_145"
 *   onFullscreenToggle={() => setFullscreen(!fullscreen)}
 *   isFullscreen={false}
 * />
 * ```
 * 
 * @see {@link ../cellChartConfigs.ts} - Chart configuration (handinOverS1CellChartConfig)
 * @see {@link ../../../components/SiteCharts.tsx} - Template component
 * @see API: /api/lte-cell/handin-over-s1?cell_name={cellName}
 */

const HandinOverS1Cell: React.FC<HandinOverS1CellProps> = ({
  cellName,
  isFullscreen,
  onFullscreenToggle
}) => {
  const additionalParams = useMemo(
    () => ({
      cell_name: cellName
    }),
    [cellName]
  );

  return (
    <SiteCharts
      config={handinOverS1CellChartConfig}
      additionalParams={additionalParams}
      isFullscreen={isFullscreen}
      onFullscreenToggle={onFullscreenToggle}
    />
  );
};

export default HandinOverS1Cell;
